/**
 * Shared label helpers for tool and toolset status/kind display.
 * Centralizes duplicate label logic from ToolsTab, ToolsetsTab,
 * ToolDetailsDrawer, and ToolsetDetailsDrawer.
 */

export type StatusFilter = 'all' | 'active' | 'paused' | 'draft';

export function getStatusLabel(status: string): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'paused':
      return 'Paused';
    case 'init':
    case 'draft':
      return 'Draft';
    case 'retired':
      return 'Retired';
    default:
      return status || 'Unknown';
  }
}

export function getToolKindLabel(kind?: string): string {
  switch (kind) {
    case 'nexset_read':
      return 'Read';
    case 'nexset_action':
      return 'Action';
    case 'custom_tool':
      return 'Custom';
    default:
      return kind || '';
  }
}

/**
 * Derive a human-readable display name from tool metadata.
 * Formats machine-style names (e.g. "datasets.foo.read") into
 * readable labels (e.g. "Read Foo").
 */
export function getToolDisplayName(tool: {
  name?: string;
  tool_name?: string;
  tool_id?: number;
  id?: number;
}): string {
  const rawName = tool.name || tool.tool_name;
  if (rawName) return formatMachineName(rawName);

  return `Tool ${tool.tool_id || tool.id || 'Unknown'}`;
}

function formatMachineName(name: string): string {
  // Only reformat names that match known machine-name patterns.
  // The dot check requires lowercase/digits/underscores between dots to
  // avoid mangling human-readable names like "Version 2.0" or "Dr. Smith".
  const isMachineDotName = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*){2,}$/.test(name);
  const isNexsetPrefixed = /^nexset_\d+_/.test(name);
  if (!isMachineDotName && !isNexsetPrefixed) return name;

  // "datasets.newsapi_org_sources.read" → "Read Newsapi Org Sources"
  const dotParts = name.split('.');
  if (dotParts.length >= 3) {
    const operation = dotParts[dotParts.length - 1];
    const slug = dotParts.slice(1, -1).join('_');
    const readable = slug
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    const readableOp = operation
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    return `${readableOp} ${readable}`;
  }

  // "nexset_12345_read" → "Read Nexset 12345"
  const match = name.match(/^nexset_(\d+)_(\w+)$/);
  if (match) {
    const op = match[2]
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    return `${op} Nexset ${match[1]}`;
  }

  return name.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
